import Dialog from "../../../General/ConfirmableDialog";
import React, { useState } from "react";
import { useDrag } from "react-dnd";
import {
  Card,
  CardContent,
  Typography,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import { DragItem, ItemTypes, Lead } from "../types/LeadBoardTypes";
import useAuth from "../../../../hooks/useAuth";
import { apiVariable } from "../../../../api/apiVariable";
import { useQueryClient } from "@tanstack/react-query";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import UpdateLeadPopup from "../UpdateLeadPopup/UpdateLeadPopup";

interface LeadCardProps {
  lead: Lead;
  overdueLeads?: Record<string, Lead>;
  onDoubleClick: (lead: Lead) => void;
}

const LeadCard: React.FC<LeadCardProps> = React.memo(
  ({ lead, overdueLeads = {}, onDoubleClick }) => {
    const [{ isDragging }, drag] = useDrag<
      DragItem,
      void,
      { isDragging: boolean }
    >(() => ({
      type: ItemTypes.LEAD,
      item: { id: lead.id, currentStatus: lead.status },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }));

    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const { token } = useAuth();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleSnackbarClose = () => {
      setStatus("idle");
      setErrorMessage(null);
    };

    const handleDeleteConfirmOpen = () => {
      setConfirmDeleteOpen(true);
    };

    const handleDeleteConfirmClose = () => {
      setConfirmDeleteOpen(false);
    };

    const handleDelete = async () => {
      try {
        await axios.delete(`${apiVariable}/lead/${lead.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setStatus("success");
        queryClient.invalidateQueries({ queryKey: ["leads"] });
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.response?.message || "Failed to delete lead");
        console.error("Failed to delete lead:", error);
      } finally {
        handleMenuClose();
        handleDeleteConfirmClose();
      }
    };

    return (
      <>
        <Card
          ref={drag}
          onDoubleClick={() => onDoubleClick(lead)}
          sx={{
            opacity: isDragging ? 0.5 : 1,
            marginBottom: 1,
            cursor: "move",
            width: 250,
            maxWidth: "100%",
            borderColor: lead.isWarm ? "orange" : "transparent",
            border: overdueLeads[lead.id]
              ? "2px solid red"
              : lead.isWarm
                ? "2px solid orange"
                : "2px solid #e0e0e0",
            backgroundColor: overdueLeads[lead.id] ? "#ffe6e6" : "white", // Optional background for overdue
          }}
          variant="outlined"
        >
          <Button
            onClick={() => onDoubleClick(lead)}
            size="small"
            sx={{ display: { xs: "inline-flex", lg: "none" } }}
          >
            View lead
          </Button>
          <CardHeader
            title={lead.company}
            subheader={lead.city}
            action={
              <>
                <IconButton onClick={handleMenuOpen} aria-label="settings">
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem>
                    <UpdateLeadPopup lead={lead} />
                  </MenuItem>
                  <MenuItem onClick={handleDeleteConfirmOpen}>
                    <Button variant="contained" color="error">
                      Delete
                    </Button>
                  </MenuItem>
                </Menu>
              </>
            }
            titleTypographyProps={{ variant: "subtitle2", noWrap: true }}
            subheaderTypographyProps={{ variant: "caption", noWrap: true }}
            sx={{
              padding: "8px",
              "& .MuiCardHeader-content": {
                overflow: "hidden",
              },
            }}
          />
          <CardContent sx={{ padding: "4px 8px" }}>
            <Typography variant="caption" color="textSecondary">
              <strong>DOT:</strong> {lead.dot}
            </Typography>
            <br />

            {lead.mc && (
              <Typography variant="caption" color="textSecondary">
                <strong>MC:</strong> {lead.mc}
              </Typography>
            )}
            <br />

            {lead.contactMethod && (
              <Typography variant="caption" color="textSecondary">
                <strong>Contact Method:</strong> {lead.contactMethod}
              </Typography>
            )}
            <br />

            {lead.assignedTo && (
              <Typography variant="caption" color="textSecondary">
                <strong>Assigned To:</strong> {lead.assignedTo}
              </Typography>
            )}
            <br />

            {lead.contactedDate && (
              <Typography variant="caption" color="textSecondary">
                <strong>Contacted Date:</strong>{" "}
                {new Date(lead.contactedDate).toLocaleDateString()}
              </Typography>
            )}
            <br />

            {lead.interestedIn && (
              <Typography variant="caption" color="textSecondary">
                <strong>Interested In:</strong> {lead.interestedIn}
              </Typography>
            )}
            <br />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              {lead.hasComments && (
                <Tooltip title="Has Comments">
                  <CommentIcon
                    fontSize="small"
                    sx={{ color: "blue", marginRight: 1 }}
                  />
                </Tooltip>
              )}
              {lead.isProcessed && (
                <Tooltip title="The lead was processed">
                  <CheckCircleIcon fontSize="small" sx={{ color: "green" }} />
                </Tooltip>
              )}
            </Box>
          </CardContent>
        </Card>

        <Dialog open={confirmDeleteOpen} onClose={handleDeleteConfirmClose}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this lead? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteConfirmClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>

        <StatusSnackbar
          status={status}
          successMessage="Lead deleted successfully"
          errorMessage={errorMessage}
          handleClose={handleSnackbarClose}
        />
      </>
    );
  },
);

export default LeadCard;
